import {
  fetchOrderPreview,
  forgetLastOrderPreview,
  hasValidDataForLineItemSubtotal,
  processOrderPreviewResponseForSummary,
  ServerRespSummaryData,
  ShippingOption,
  OrderPreviewPayload,
} from './summary'
import {
  Stripe,
  StripeAddressElementOptions,
  StripeElements,
  StripeElementsOptionsMode,
  StripeElementType,
  StripeExpressCheckoutElementOptions,
  StripeLinkAuthenticationElementOptions,
  StripePaymentElementOptions,
  BillingDetails,
  ChangeResolveDetails,
  ClickResolveDetails,
  CssFontSource,
  CustomFontSource,
  StripeError,
  ConfirmationToken,
  StripeAddressElementChangeEvent,
  ShippingAddress,
  DefaultValuesOption,
  PaymentIntentResult,
  PaymentIntentOrSetupIntentResult,
} from '@stripe/stripe-js'

import { CouponStates, FORM_STRUCTURE_BY_TOTAL_STEPS, stripeElementsForms, SummaryStates } from './consts'
import { getStripeCheckoutErrorMessage, StripeCheckoutErrorCode } from './errors'
import {
  CheckoutData,
  CheckoutDataAddress,
  FormName,
  State,
  StripeAddress,
  StripeCheckoutStore,
  StripeComponent,
  StripeElementOptions,
  StripeMountedElements,
  StripeMountedElementsData,
  StripeSessionData,
  SubmitPayload,
  SubmitResponse,
  ParsedAddressEventData,
} from './types'
import {
  CFFetcherError,
  FetcherResponse,
  FetcherResponseError,
  isBackendError,
  isFetcherError,
  isResponseError,
  MANUALLY_ABORTED,
} from 'javascript/lander/utils/fetcher'
import { uuidv4 } from 'javascript/lander/cf_utils'
import {
  hasPhysicalProducts,
  setupOrderSummaries,
  buildCustomFields,
  checkValidCustomFields,
  scrollToFirstVisibleError,
  collectLeads,
} from '../Services/checkout-utils'
import {
  serverCheckoutErrorMessage,
  isActionableCheckoutError,
  isRefusedCouponError,
} from '../Services/checkout-error-classification'
import { ComponentGroup } from 'javascript/lander/runtime'
import {
  convertAddressFromStripe,
  getStripeMode,
  parseCssVars,
  getCartEstimatedTotal,
  calculateStripeChangeResolveDetails,
  calculateStripeClickResolveDetails,
  confirmationTokenBillingName,
  STRIPE_SKIP_SHIPPING_ID,
  convertToStripeAddress,
  convertAddressFromSession,
  hasUpgradeDowngradeItems,
  garlicRetrieve,
  garlicStore,
  observeStripeElement,
  showInputError,
  clearInputError,
} from './utils'
import {
  CheckoutFormErrors,
  hasErrors,
  productErrors,
  shippingOptionErrors,
  tosErrors,
  contactErrors,
  addressErrors,
} from '../RebillyCheckout/checkout-globals'

import { Address, CheckoutCart, CheckoutShippingProfileSelectUpdate, SummaryLineItem } from '../types'
import CheckoutShippingProfileSelectV1 from 'blueprints/checkout-shipping-profile-select-v1'
import CheckoutMultiStepV2 from 'blueprints/checkout-multi-step-v2'
import CheckoutLoadingV1 from 'blueprints/checkout-loading-v1'
import CheckoutSubmitButtonV1 from 'blueprints/checkout-submit-button-v1'
import CheckoutTosV1 from 'blueprints/checkout-tos-v1'
import { isStripeError, postSubmit, stripeSessionApiFetcher } from './api'
import CheckoutConfigurationErrorV1 from 'blueprints/checkout-configuration-error-v1'
import { ensureCheckoutStoreInitialized } from './store'
import { buildStripeFonts, ensureStripeInitialized, stripeCurrency, stripeInstallationId } from './stripe'
import CheckoutSubmitNotificationV1 from 'blueprints/checkout-submit-notification-v1'
import { CheckoutExistingOrders } from '../Services/checkout-existing-orders'
import { CheckoutCriticalError } from '../Services/checkout-critical-errors'
import { CheckoutStates } from './enums'
import { IntlTel_initPhoneInput } from 'javascript/lander/cf_utils'

export function createInstance(component: StripeComponent): StripeCheckoutLogic {
  const stripe = ensureStripeInitialized()
  const store = ensureCheckoutStoreInitialized()

  // TODO: We need to get the checkout state on the SSR in workers otherwise
  // the element will start in guest mode and transition afterwards
  return new StripeCheckoutLogic(component, stripe, store)
}

const CHECKOUT_STATES = Object.values(CheckoutStates)
// Stripe `error.code` values that mean the stored instrument itself was refused. Only these should
// surface a card field on a checkout that has none: hasPaymentElement() is also false for an
// upgrade/downgrade cart, and there a 3DS dismissal (`authentication_required`) must keep retrying
// the stored instrument rather than demand a new card. Mirrors the "declined" rows of
// app/concerns/payment_platforms/stripe/charge_mappings.rb. `insufficient_funds` is deliberately
// absent: Stripe sends it as `decline_code` under `card_declined`, never as `error.code`.
const STRIPE_INSTRUMENT_REJECTION_CODES = new Set([
  'card_declined',
  'card_not_supported',
  'currency_not_supported',
  'expired_card',
  'incorrect_cvc',
  'incorrect_number',
  'incorrect_zip',
  'processing_error',
])

export class StripeCheckoutLogic {
  _stripeData: StripeMountedElements

  guestInputFields: Record<string, HTMLInputElement> = {}
  paymentDeclined = false
  submitedOnce = false
  checkoutData = { contact: {}, address: {} } as CheckoutData
  digitalPaymentData = { contact: {}, address: {}, shippingRateId: '' } as any
  parsedAppearence: any

  summaryTotalAmountCents: number
  summaryDiscountAmountCents: number
  summaryLineItems: SummaryLineItem[]

  stripeElementOptions: StripeElementsOptionsMode

  stripeFonts: Array<CssFontSource | CustomFontSource>

  shippingComponents: ComponentGroup<CheckoutShippingProfileSelectV1>
  selectedShippingOption: ShippingOption
  errors: CheckoutFormErrors

  element: HTMLElement

  loadingComponents: ComponentGroup<CheckoutLoadingV1>
  submitButtons: ComponentGroup<CheckoutSubmitButtonV1>
  submitNotifications: ComponentGroup<CheckoutSubmitNotificationV1>
  sendOrderPreviewTimer: NodeJS.Timeout

  lastSubmitFailure: { message: string; actionable: boolean } | null = null

  setupErrors: StripeCheckoutErrorCode[]
  oldHasPhysical: boolean
  preventOrderPreview = false
  submitInFlight = false
  // Identity-tracked entry the summary subscriber pushes into `errors.globalErrors` so we
  // can remove exactly it without clobbering globals set by submit-time flows.
  summaryErrorEntry: { message: string } | null = null

  couponStateBeforeApplying
  sentToTracking = { email: null, firstName: null, lastName: null, phone: null }
  trackingTimer: NodeJS.Timeout

  constructor(private component: StripeComponent, private stripe: Stripe, private store: StripeCheckoutStore) {
    this._stripeData = {}
    CHECKOUT_STATES.forEach((state) => {
      this._stripeData[state] = {
        stripeElements: null,
        mountedElements: {
          shipping: {},
          billing: {},
          expressCheckout: {},
          linkAuthentication: {},
          payment: {},
        },
      }
    })

    this.element = component.element
    this.errors = { globalErrors: [], fields: {} }
    this.setupErrors = []

    this.parsedAppearence = parseCssVars(component.element, this.component.appearance)
    this.stripeFonts = buildStripeFonts()
    const cart = this.store.checkoutCart.get()

    if (globalThis.sessionData.contact.email) {
      this.checkoutData.contact.email = globalThis.sessionData.contact.email
    }
    this.updateWithShippingAddress(cart)
    // NOTE: tmp dev tools
    globalThis.montedElements = this.montedElements

    console.log('[Stripe] appearance => ', this.parsedAppearence)
  }

  private triggerTrackingForStripeElement(data: {
    email?: string
    firstName?: string
    lastName?: string
    phone?: string
  }): void {
    clearTimeout(this.trackingTimer)
    this.trackingTimer = setTimeout(() => {
      try {
        if (data.email && this.sentToTracking.email !== data.email) {
          this.sentToTracking.email = data.email
          this.createTrackingInput('email', data.email)
        }

        if (data.firstName && this.sentToTracking.firstName !== data.firstName && !this.guestInputFields.first_name) {
          this.sentToTracking.firstName = data.firstName
          this.createTrackingInput('first_name', data.firstName)
        }

        if (data.lastName && this.sentToTracking.lastName !== data.lastName && !this.guestInputFields.last_name) {
          this.sentToTracking.lastName = data.lastName
          this.createTrackingInput('last_name', data.lastName)
        }

        if (data.phone && this.sentToTracking.phone !== data.phone && !this.guestInputFields.phone_number) {
          this.sentToTracking.phone = data.phone
          this.createTrackingInput('phone_number', data.phone)
        }
      } catch (error) {
        console.error('[Stripe] Tracking helper error:', error)
      }
    }, 200)
  }

  private createTrackingInput(name: string, value: string): void {
    const input = document.createElement('input')
    input.type = name.includes('email') ? 'email' : 'text'
    input.name = name
    input.value = value
    input.style.cssText = 'position:absolute;left:-9999px;opacity:0;pointer-events:none'

    document.body.appendChild(input)
    input.dispatchEvent(new Event('focusout', { bubbles: true }))
    input.dispatchEvent(new Event('change', { bubbles: true }))
    input.dispatchEvent(new Event('blur', { bubbles: true }))
    document.body.removeChild(input)
  }

  async ensureStripeSession(): Promise<StripeSessionData | null> {
    const startedAt = Date.now()
    const sessionResponse = await stripeSessionApiFetcher.post('checkouts/stripe_get_session', {
      referrer_url: window.location.href,
      integrations_stripe_installation_id: stripeInstallationId,
    })

    if (isResponseError(sessionResponse)) {
      const failureKind = this.sessionFailureKind(sessionResponse)
      const durationMs = Date.now() - startedAt

      // Supersession is not a failure. `stripeSessionApiFetcher` is a module singleton with
      // `onlyOneInflightRequest`, so a second caller — a remount, a same-tab revisit, a
      // second Checkout element on the page — deliberately aborts this one and takes over.
      // The request that replaced us mounts the element on success and reports its own
      // failure otherwise, so banner-ing here reports a failure that did not happen. This
      // is the "first visit fine, reload shows 0x20" report on ENG-7225: nothing was slow
      // or rate limited, the loser of a supersede was simply rendering its own abort.
      // ...but only when this element is genuinely gone. Losing a supersede does not imply we
      // were discarded: `sticky.ts` used to clone a `.stickyTop` section and hydrate it, which
      // mounted a *second* StripeCheckoutLogic that aborted the still-on-the-page original's
      // session, and staying silent there left an empty payment area with no banner once the
      // clone was removed (ENG-7676 — `sticky.ts` now pins the section rather than copying it).
      // Silence the loser only if it no longer owns the element: it left the DOM, or a second
      // hydration of the same node replaced it (ENG-7975).
      if (failureKind === 'superseded' && !this.isLiveInstance()) {
        // Kept out of the 0x20 fault deliberately: distinct message, so watching how often
        // this fires does not contaminate the metric real session failures are counted by.
        globalThis.Honeybadger?.notify('StripeCheckout session superseded', {
          context: { durationMs, checkoutState: this.getState() },
        })
        return null
      }

      // The session only adds the visitor's saved payment methods and the wallet/DPM options.
      // A buyer can pay without either, so a failed session falls back to card-only elements
      // (`initializeElements(null)`) instead of a blocking banner. ENG-7975: one failed
      // request per page load left buyers on a dead "Code: 0x60" checkout. Still reported,
      // under the same fault, so the failure rate stays measurable.
      this.reportSetupError(StripeCheckoutErrorCode.API_SESSION_INITIALIZATION_ERROR, {
        failureKind,
        durationMs,
        fallback: 'card_only',
      })
      return null
    } else {
      return sessionResponse
    }
  }

  // This logic's component still owns its element: attached, and not replaced by a later
  // hydration of the same node (which leaves a second instance mounted on top of us).
  private isLiveInstance(): boolean {
    const element = this.component.element
    return element.isConnected && (element as any).cf2_instance === this.component
  }

  // Maps the fetcher error to a stable label:
  //   'timeout_error' — every attempt hit the fetcher's own 30s timer
  //   'server_error'  — the endpoint 5xx'd on every attempt
  //   'network_error' — the request itself failed
  //   'superseded'    — a later request on the same fetcher replaced this one
  private sessionFailureKind(response: FetcherResponseError): string {
    if (!isFetcherError(response)) return 'backend_error'
    // The declared type of `error` is string, but the fetcher stores whatever
    // was thrown (CFFetcherError, DOMException) or the MANUALLY_ABORTED string.
    const error: unknown = response.error
    if (error === MANUALLY_ABORTED) return 'superseded'
    if (error instanceof CFFetcherError) return error.type.toLowerCase()
    return String(error)
  }

  backfillContactData() {
    const shouldUseGarlic = this.getState() === 'guest'
    ;['first_name', 'last_name', 'email', 'phone_number', 'name'].forEach((k) => {
      if (k === 'phone_number' && !this.hasPhoneNumber()) return
      const garlicValue = shouldUseGarlic ? garlicRetrieve(k) : ''
      const sessionValue = globalThis.sessionData.contact[k]
      if (!garlicValue && !sessionValue) return

      if (k === 'name' && !this.checkoutData.contact.first_name) {
        const [first_name, ...last_names] = garlicValue.split(' ')
        this.checkoutData.contact.first_name = first_name
        this.checkoutData.contact.last_name = last_names.join(' ')
      } else {
        this.checkoutData.contact[k] = sessionValue ?? garlicValue
      }
    })
    this.syncInputFields()
  }

  updateInputState(name: string, value) {
    garlicStore(name, value)
    if (value) {
      this.guestInputFields[name].closest('[data-page-element="Input/V1"]').classList.add('hasValue')
    } else {
      this.guestInputFields[name].closest('[data-page-element="Input/V1"]').classList.remove('hasValue')
    }
  }

  storeShippingDataFromExpress(data: ShippingAddress | BillingDetails) {
    if (!data) return
    if (data.name) garlicStore('name', data.name)
    if (!data.address) return
    const address = convertAddressFromStripe(data.address)
    Object.keys(address).forEach((k) => {
      if (address[k]) garlicStore(k, address[k])
    })
  }

  syncInputFields(): void {
    Object.keys(this.guestInputFields).forEach((k) => {
      const value = this.checkoutData.contact[k]
      if (k == 'phone_number') {
        ;(this.guestInputFields[k] as any).iti.setNumber(value ?? '')
      } else {
        this.guestInputFields[k].value = value ?? ''
      }
      this.updateInputState(k, value)
    })
  }

  errorInputForInput(input): HTMLElement {
    const id = input.closest('[data-page-element]').getAttribute('id')
    return this.element.querySelector(`[data-input-id="${id}"]`)
  }

  updateWithShippingAddress(cart: CheckoutCart): void {
    this.checkoutData.address = {}

    if (hasPhysicalProducts(cart)) {
      const sessionShippingAddress =
        globalThis.sessionData.contact.shipping_addresses?.[0] ?? globalThis.sessionData.contact.billing_addresses?.[0]

      if (sessionShippingAddress) {
        this.checkoutData.address = convertAddressFromSession(sessionShippingAddress)
      }
    }

    if (this.getState() == 'oto' && !hasPhysicalProducts(cart)) {
      const sessionBillingAddress = globalThis.sessionData.contact.billing_addresses?.[0]
      if (sessionBillingAddress) {
        this.checkoutData.address = convertAddressFromSession(sessionBillingAddress)
      }
    }

    if (globalThis.sessionData.contact) {
      this.checkoutData.contact.first_name = globalThis.sessionData.contact.first_name
      this.checkoutData.contact.last_name = globalThis.sessionData.contact.last_name
      this.checkoutData.contact.phone_number = globalThis.sessionData.contact.phone_number
      this.syncInputFields()
    }
  }

  initializeElements(session: StripeSessionData | null, amount: number): void {
    const cart = this.store.checkoutCart.get()
    this.stripeElementOptions = {
      amount,
      mode: getStripeMode(cart, amount),
      currency: stripeCurrency.toLowerCase() ?? 'usd',
      appearance: this.parsedAppearence,
      loader: 'never',
      fonts: this.stripeFonts,
      locale: this.component.locale,
      ...(this.component.paymentGateway.disable_stripe_payment_method_future_usage
        ? {}
        : { setup_future_usage: 'off_session' }),
      ...(session?.can_use_dpm ? {} : { payment_method_types: ['card'] }),
      ...(session?.stripe_options ?? {}),
    }

    this.stripeElements = this.stripe.elements(this.stripeElementOptions)

    // NOTE: tmp for dev
    globalThis.stripeElements = this.stripeElements

    // NOTE: TS is not accusing to have it, but it exists:
    // https://docs.stripe.com/js/element/events/on_update_end
    ;(this.stripeElements as any).on('update-end', () => {
      this.updatingExpressEnded()
    })
  }

  // The amount every wallet sheet shows: the Express Checkout button's and the Payment Element's
  // Apple Pay / Google Pay tabs alike.
  updateElementsAmount(amountCents: number): void {
    const cart = this.store.checkoutCart.get()
    this.stripeElements.update({ amount: amountCents, mode: getStripeMode(cart, amountCents) })
  }

  mount(): void {
    if (!this.stripe) {
      this.pushSetupError(StripeCheckoutErrorCode.STRIPE_LIB_INITIALIZATION_ERROR)
      return
    }

    this.submitNotifications = this.component.getComponentGroup(CheckoutSubmitNotificationV1)

    this.detectIfReturningFailedPayment()

    this.backfillContactData()

    Promise.all([
      this.ensureStripeSession(),
      CheckoutExistingOrders.fetch({
        integrations_stripe_installation_id: stripeInstallationId,
      })
        .catch((error) => {
          // A wrapped MANUALLY_ABORTED means another StripeCheckoutLogic superseded this
          // fetch through the singleton `existingOrderFetcher` — a remount, a same-tab
          // revisit, a second Checkout element on the page. The winner's fetch populates
          // CheckoutExistingOrders' shared static state, so treat it as success here:
          // rejecting would fail the Promise.all and banner MOUNT_REJECTED on a checkout that
          // is working. Every other cause is a genuine failure and still rejects into the
          // banner below.
          //
          // `cause` is declared as Error, but the fetcher throws the MANUALLY_ABORTED
          // string — the same declared-type lie `sessionFailureKind` works around.
          const cause: unknown = error instanceof CheckoutCriticalError ? error.cause : null
          if (cause !== MANUALLY_ABORTED) throw error
        })
        .then(() => {
          this.updateSubmitButton()
        }),
    ])
      .then(([sessionData]) => {
        // A null session is a failed one (card-only fallback) or the loser of a supersede
        // that no longer owns its element; only the latter has nothing left to mount.
        if (this.isLiveInstance()) {
          const cart = this.store.checkoutCart.get()
          const intialAmount = getCartEstimatedTotal(cart)

          this.initializeElements(sessionData, intialAmount)
          this.sendOrderPreview()
          // NOTE: The timeout here is to fix EDI-2607, the issue is on stripe's side
          // but we found this workaround.
          setTimeout(() => {
            this.oneTimeListeners()
          }, 1000)
        }
      })
      // Without this, a rejection here (CheckoutExistingOrders.fetch throws a
      // CheckoutCriticalError rather than returning an error response) skipped
      // element mounting silently: no banner, no notice, checkout spins forever.
      .catch((error) => {
        this.pushSetupError(StripeCheckoutErrorCode.MOUNT_REJECTED_ERROR, undefined, {
          failureKind: 'mount_rejected',
          mountError: String(error?.message ?? error),
          // Keep the stack and any wrapped cause: the notify message is held constant per
          // code for grouping, so context is the only place the specific failure survives.
          mountErrorStack: error?.stack,
          mountErrorCause: error?.cause ? String(error.cause?.message ?? error.cause) : undefined,
        })
      })

    setupOrderSummaries(this.component)
    this.component.setHandlers(CheckoutMultiStepV2, {
      onValidateStep: (step) => this.validateStep(step),
      onStepChange: (oldStep, step) => {
        // NOTE: The update here is to fix EDI-2593 and EDI-2609, the issue is on stripe's side
        // but we found this workaround.
        this.stripeElements.update({ appearance: this.parsedAppearence })
        const formStructure = FORM_STRUCTURE_BY_TOTAL_STEPS[this.getTotalSteps()]
        if (formStructure[oldStep].includes('contact') && oldStep < step) {
          collectLeads(this.checkoutData.contact)
        }
      },
    })

    this.component.setHandlers(CheckoutTosV1, {
      onAcceptedChange: (accepted) => {
        if (this.submitedOnce && accepted) {
          this.clearErrors('tos')
          this.showErrors()
        }
      },
    })

    this.shippingComponents = this.component.getComponentGroup(CheckoutShippingProfileSelectV1, {
      onSelectOption: (option) => this.selectShippingOption(option),
    })

    window.addEventListener('ON_APPLY_COUPON', (event: CustomEvent) => {
      this.sendOrderPreview(event.detail)
    })

    window.addEventListener('ON_CLEAR_COUPON', (event: CustomEvent) => {
      this.sendOrderPreview(event.detail)
    })

    this.loadingComponents = this.component.getComponentGroup(CheckoutLoadingV1)
    this.submitButtons = this.component.getComponentGroup(CheckoutSubmitButtonV1, {
      // NOTE: returned so the button can tell a finished attempt from one that is
      // still running, and unlock itself when we never got as far as the POST.
      onSubmit: () => this.handleSubmit(this.selectedShippingOption),
    })
  }

  detectIfReturningFailedPayment(): void {
    const urlParams = new URLSearchParams(window.location.search)
    const failedPaymentMessage = urlParams.get('failed_payment_message')
    if (failedPaymentMessage) {
      const decodedFailedMessage = decodeURIComponent(failedPaymentMessage.replace(/\+/g, ' '))
      this.handleError({ message: decodedFailedMessage }, true)
      const visibleSubmitButton = this.getStateElement().querySelector("[data-page-element='CheckoutSubmitButton/V1']")
      $('html, body')
        .stop(true, true)
        .animate(
          {
            scrollTop: $(visibleSubmitButton).offset().top - 100,
          },
          500
        )
      this.submitedOnce = true
    }
  }

  submitCheckout(redirectOverride?: string): Promise<boolean> {
    return this.handleSubmit(this.selectedShippingOption, false, redirectOverride)
  }

  clearErrors(field?: FormName): void {
    this.errors.globalErrors = []
    if (field) {
      delete this.errors.fields[field]
    }
  }

  clearAllErrors(): void {
    this.errors.globalErrors = []
    this.errors.fields = {}
  }

  pushGlobalError(message: string): void {
    this.errors.globalErrors.push({ message })
  }

  pushFieldError(field: string, message: string): void {
    this.errors.fields[field] = { message }
  }

  showErrors({ shouldScrollToError } = { shouldScrollToError: false }): void {
    const stepStore = this.store.checkout.step
    const currentStep = stepStore.get()
    const totalSteps = this.getTotalSteps()

    let wrapperStateElement = this.getStateElement()
    let hasFieldErrors = false

    let firstFieldWithError = null
    const formStructure = FORM_STRUCTURE_BY_TOTAL_STEPS[totalSteps]
    for (let stepIndex = 0; stepIndex < formStructure.length; stepIndex++) {
      const stepFields = formStructure[stepIndex]
      for (const field of stepFields) {
        if (this.errors.fields[field]) {
          hasFieldErrors = true
          if (stepIndex !== currentStep) stepStore.set(stepIndex)
          if (!firstFieldWithError) firstFieldWithError = field
          break
        }
      }
    }

    if (totalSteps > 1) {
      wrapperStateElement = wrapperStateElement.querySelector(
        `.elMultiStepBody[data-step-number="${stepStore.get() + 1}"]`
      )
    }

    Object.keys(this.guestInputFields).forEach((field) => {
      if (this.errors.fields[field]) {
        showInputError(this.errorInputForInput(this.guestInputFields[field]), this.errors.fields[field].message)
      }
    })

    const generalErrorsWrapper = wrapperStateElement.querySelector<HTMLElement>('.checkout-general-errors-wrapper')

    // The checkout template renders a `.checkout-general-errors-wrapper` inside every
    // step body and every checkout-state container, so several exist in the DOM at
    // once. Reset all of them so a message previously written into a now-hidden step
    // or state doesn't linger and re-appear when the user navigates back.
    this.component.element.querySelectorAll<HTMLElement>('.checkout-general-errors-wrapper').forEach((w) => {
      w.classList.add('forceHide')
      const span = w.querySelector('span')
      if (span) span.innerHTML = ''
    })

    const hasGlobalErrors = this.errors.globalErrors.length && this.errors?.globalErrors?.[0]?.message
    if (hasGlobalErrors) {
      const errorMessage = this.errors.globalErrors[0].message
      generalErrorsWrapper.querySelector('span').innerHTML = errorMessage
      generalErrorsWrapper.classList.remove('forceHide')
    }

    if (shouldScrollToError && (hasGlobalErrors || hasFieldErrors)) {
      if (stripeElementsForms.includes(firstFieldWithError)) {
        const scrollToElement = wrapperStateElement.querySelector<HTMLElement>(`.stripe-${firstFieldWithError}`)
        scrollToFirstVisibleError(scrollToElement)
      } else {
        scrollToFirstVisibleError(generalErrorsWrapper)
      }
    }
  }

  removeSubmitNotification(): void {
    this.submitNotifications.setStatus('', '', '')
  }

  setSubmitNotification(text: string, subtext?: string, extraText?: string): void {
    this.submitNotifications.setStatus(text, subtext, extraText)
  }

  get montedElements(): StripeMountedElementsData {
    return this._stripeData[this.getState()].mountedElements
  }
  get stripeElements(): StripeElements {
    return this._stripeData[this.getState()].stripeElements
  }
  set stripeElements(stripeElement: StripeElements) {
    this._stripeData[this.getState()].stripeElements = stripeElement
  }

  getState(): State {
    return this.component.element.dataset.currentState as State
  }
  setState(state: State): void {
    this.component.element.dataset.currentState = state
  }

  updatingExpressStarted(): void {
    this.getStateElement().querySelector('.stripe-express-loader-wrapper')?.classList.remove('forceHide')
    this.getStateElement().querySelector('.stripe-expressCheckout')?.classList.add('elHidden')
  }
  updatingExpressEnded(): void {
    this.getStateElement().querySelector('.stripe-express-loader-wrapper')?.classList.add('forceHide')
    this.getStateElement().querySelector('.stripe-expressCheckout')?.classList.remove('elHidden')
  }

  getStateElement(): HTMLElement {
    return this.component.element.querySelector(`[data-state="${this.component.element.dataset.currentState}"]`)
  }

  unmountElement(name: keyof StripeMountedElementsData): void {
    if (!this.montedElements[name].element) return
    this.montedElements[name].element.unmount()
    const stripeElement = this.getStateElement().querySelector(`.stripe-${name}`) as HTMLElement
    delete stripeElement?.style.opacity
    this.montedElements[name].lastChangeEvent = null
    this.montedElements[name].mounted = false
  }

  mountElement(
    name: 'address',
    options?: StripeAddressElementOptions,
    overrideSelector?: string,
    overrideMountName?: string
  ): void
  mountElement(name: 'linkAuthentication', options?: StripeLinkAuthenticationElementOptions): void
  mountElement(name: 'expressCheckout', options?: StripeExpressCheckoutElementOptions): void
  mountElement(name: 'payment', options?: StripePaymentElementOptions): void
  mountElement(
    name: StripeElementType,
    options?: StripeElementOptions,
    overrideSelector?: string,
    overrideMountName?: string
  ): void {
    console.log('[Stripe] mounteElement =>', name, options)
    const state = this.getState()
    const selector = overrideSelector ?? `.stripe-${name}`

    const hasElement = this.getStateElement().querySelector(selector)
    if (!hasElement) return

    const mountName = overrideMountName ?? name

    if (!this.montedElements[mountName].element) {
      options = options ?? {}
      this.montedElements[mountName].element = this.stripeElements.create(name as any, options as any)
    }
    this.montedElements[mountName].element.mount(`[data-state=${state}] ${selector}`)
    this.montedElements[mountName].mounted = true
  }

  mountLink(): void {
    if (this.montedElements.linkAuthentication.mounted) return

    const options: StripeLinkAuthenticationElementOptions = {}
    if (this.checkoutData.contact.email) {
      options.defaultValues = { email: this.checkoutData.contact.email }
    }
    this.mountElement('linkAuthentication', options)
    this.montedElements.linkAuthentication.element?.on('change', (event) => {
      this.removeSubmitNotificationStripeElementChange()
      console.log('[Stripe] linkAuthentication [incoming-event]: ', event)
      delete this.errors.fields['linkAuthentication']
      this.montedElements.linkAuthentication.lastChangeEvent = event
      if (!event.complete) return
      this.checkoutData.contact.email = event.value.email
      this.triggerTrackingForStripeElement({ email: event.value.email })
      this.updatePaymentElementDefaultValues()
    })

    this.montedElements.linkAuthentication.element?.on('ready', () => {
      this.elementReady('linkAuthentication')
      this.elementReady('contact-form')
    })
    this.montedElements.linkAuthentication.element?.on('loaderror', (error) =>
      this.handleStripeElementLoadingError(error)
    )
  }

  pushSetupError(code: StripeCheckoutErrorCode, customMessage?: string, context?: Record<string, unknown>): void {
    this.setupErrors.push(code)

    const setupErrorComponent = this.component.getComponent(CheckoutConfigurationErrorV1)
    const errorMessage = customMessage || getStripeCheckoutErrorMessage(this.setupErrors)
    setupErrorComponent.setErrors(errorMessage)
    scrollToFirstVisibleError(setupErrorComponent.element)

    this.reportSetupError(code, context)
  }

  reportSetupError(code: StripeCheckoutErrorCode, context?: Record<string, unknown>): void {
    // Keep the notify message constant per code so Honeybadger groups all
    // occurrences of one code into one fault.
    globalThis.Honeybadger?.notify(`StripeCheckout setup error: ${StripeCheckoutErrorCode[code]}`, {
      context: {
        errorCode: `0x${code.toString(16)}`,
        // The banner shows the OR of all codes pushed so far. Report it too so
        // a banner screenshot (e.g. "Code: 0x24") can be matched to notices.
        // Codes reported without a banner (a session failure) are not in it.
        errorMask: `0x${this.setupErrors.reduce((acc, val) => acc | val, 0).toString(16)}`,
        checkoutState: this.getState(),
        hasContactEmail: Boolean(globalThis.sessionData?.contact?.email),
        ...(context ?? {}),
      },
    })
  }

  handleStripeElementLoadingError({ elementType, error }: { elementType: string; error: StripeError }) {
    const errorEnumName = `STRIPE_${elementType
      .split(/(?=[A-Z])/)
      .join('_')
      .toUpperCase()}_ELEMENT_INITIALIZATION_ERROR`
    const errorCode = StripeCheckoutErrorCode[errorEnumName]

    // Use the actual Stripe error message if available, otherwise fall back to generic message
    const errorMessage = error?.message
      ? `Error loading ${elementType} element: ${error.message}`
      : getStripeCheckoutErrorMessage([errorCode])

    this.pushSetupError(errorCode, errorMessage, { elementType, stripeError: error })
    console.error(`Stripe ${elementType} element initialization error: `, error)
  }

  getTotalSteps(): number {
    return this.getState() !== 'guest' ? 1 : this.component.totalSteps
  }

  mountAddress(): void {
    const options = this.addressOptions
    const mode = options.mode

    const state = this.getState()

    if (this.montedElements[mode].mounted) return

    if (mode === 'billing' && state === CheckoutStates.OTO && !this.paymentDeclined) return

    const stateElement = this.getStateElement()
    const otherMode = options.mode == 'shipping' ? 'billing' : 'shipping'
    stateElement.querySelector(`.stripe-address-${otherMode}-container`)?.classList.add('forceHide')
    this.unmountElement(otherMode)

    if (mode === 'billing' && !this.component.billingEnabled) {
      const contactFormElement = stateElement.querySelector('.stripe-contact-form') as HTMLElement
      contactFormElement?.classList.remove('forceHide')
      return
    }
    if (mode === 'shipping' && !this.component.billingEnabled && this.component.totalSteps === 1) {
      const contactFormElement = stateElement.querySelector('.stripe-contact-form') as HTMLElement
      contactFormElement?.classList.add('forceHide')
    }

    const stripeElement = stateElement.querySelector(`.stripe-${mode}`) as HTMLElement
    stateElement.querySelector('[data-stripe-loading-element="address"]').classList.remove('forceHide')
    stripeElement.classList.add('elAbsolute')
    delete stripeElement.style.opacity

    stateElement.querySelector(`.stripe-address-${mode}-container`)?.classList.remove('forceHide')

    this.mountElement('address', options, `.stripe-${mode}`, mode)
    this.montedElements[mode].element?.on('change', (event) => {
      this.removeSubmitNotificationStripeElementChange()
      this.montedElements[mode].lastChangeEvent = event
      delete this.errors.fields[mode]

      const parsedData = this.parsedAddressEventData(event)
      console.log('[Stripe] address [incoming-event]: ', event)

      this.checkoutData.contact.first_name = parsedData.first_name
      this.checkoutData.contact.last_name = parsedData.last_name
      this.checkoutData.contact.phone_number = parsedData.phone_number
      this.triggerTrackingForStripeElement({
        firstName: parsedData.first_name,
        lastName: parsedData.last_name,
        phone: parsedData.phone_number,
      })
      this.syncInputFields()
      this.updatePaymentElementDefaultValues()
      this.checkoutData.address = parsedData.address

      if (hasPhysicalProducts(globalThis.Checkout.computed.checkoutCart.get())) {
        if (this.hasValidAddress(this.checkoutData.address)) {
          this.shippingComponents.setLoading()
        } else {
          this.shippingComponents.setInvalidAddress()
        }
      }
      this.sendOrderPreview({ throttle: true })
    })

    this.montedElements[mode].element?.on('ready', () => {
      stateElement.querySelector(`.stripe-address-${mode}-container`).classList.remove('forceHide')
      this.elementReady(mode)
    })

    this.montedElements[mode].element?.on('loaderror', (error) => this.handleStripeElementLoadingError(error))
  }

  parsedAddressEventData(event: StripeAddressElementChangeEvent): ParsedAddressEventData {
    const data = {} as ParsedAddressEventData
    if (event.value.firstName || event.value.lastName) {
      data.first_name = event.value.firstName
      data.last_name = event.value.lastName
    } else {
      const [first_name, ...last_name] = (event.value?.name ?? '').split(' ')
      data.first_name = first_name
      data.last_name = last_name.join(' ')
    }
    data.phone_number = event.value.phone
    data.address = convertAddressFromStripe(event.value.address)
    return data
  }

  get paymentOptions(): StripePaymentElementOptions {
    let address: any = 'auto'
    if (!this.component.billingEnabled && this.component.billingFields === 'if_required') {
      address = 'if_required'
    }
    return {
      defaultValues: this.generatePaymentDefaultValues(),
      fields: {
        billingDetails: {
          name: 'never',
          phone: 'auto',
          address,
        },
      },
    }
  }
  mountPayment(): void {
    if (this.montedElements.payment.mounted) return
    const options: StripePaymentElementOptions = {
      terms: { card: 'never' },
      ...this.paymentOptions,
    }

    this.mountElement('payment', options)
    this.montedElements.payment.element?.on('change', (event) => {
      this.removeSubmitNotificationStripeElementChange()

      delete this.errors.fields['payment']
      console.log('[Stripe] paymentMethods [incoming-event]: ', event)
      this.montedElements.payment.lastChangeEvent = event
      // NOTE: With the option to disable the address element we might
      // Get some address fields in the payment element so we need to
      // send order preview after changing it
      this.sendOrderPreview({ throttle: true })
    })
    this.montedElements.payment.element?.on('ready', () => {
      this.elementReady('payment')
    })
    this.montedElements.payment.element?.on('loaderror', (error) => this.handleStripeElementLoadingError(error))
  }

  generatePaymentDefaultValues(): DefaultValuesOption {
    const defaultValues = {} as DefaultValuesOption

    if (this.checkoutData.contact.first_name || this.checkoutData.contact.last_name) {
      defaultValues.billingDetails = defaultValues?.billingDetails ?? {}
      defaultValues.billingDetails.name = [
        this.checkoutData.contact.first_name ?? '',
        this.checkoutData.contact.last_name ?? '',
      ]
        .filter(Boolean)
        .join(' ')
    }

    if (this.checkoutData.contact.email) {
      defaultValues.billingDetails = defaultValues?.billingDetails ?? {}
      defaultValues.billingDetails.email = this.checkoutData.contact.email
    }

    if (this.checkoutData.contact.phone_number && this.hasPhoneNumber()) {
      defaultValues.billingDetails = defaultValues?.billingDetails ?? {}
      defaultValues.billingDetails.phone = this.checkoutData.contact.phone_number
    }
    return defaultValues
  }

  updatePaymentElementDefaultValues(): void {
    if (this.getState() !== 'guest') return

    this.montedElements.payment.element?.update({
      ...this.paymentOptions,
    })
  }

  handleOrderPreviewForExpressCheckout(
    response: ServerRespSummaryData,
    resolve: (resolveDetails?: ChangeResolveDetails) => void
  ): void {
    this.updateElementsAmount(response.total_amount_cents)
    const changeResolveDetails = calculateStripeChangeResolveDetails(
      response.line_items,
      response.shipping_quotes_response?.options,
      response.selected_shipping_option,
      response.total_amount_cents,
      response.discount_amount_cents
    )
    resolve(changeResolveDetails)
  }

  // What the wallet sheet asks for. Express Checkout has no billing-address-change event, so the
  // shipping address is the only address the sheet can collect before `confirm` — and the preview
  // round trip it triggers is also the only thing that adds that address's tax and shipping to the
  // sheet. Ask for it unless neither is needed.
  expressClickResolveDetails(): ClickResolveDetails {
    const cart = this.store.checkoutCart.get()
    const summary = this.store.summary.get()
    const totalAmountCents = summary.data?.total?.amount_cents
    // The button is clickable before the first preview settles; without a final total to show,
    // fall back to the address round trip rather than authorize a stale amount.
    const finalTotalKnown = summary.state == SummaryStates.OK && totalAmountCents != null
    const needsAddress =
      hasPhysicalProducts(cart) || !!this.component.paymentGateway.stripe_tax_enabled || !finalTotalKnown

    if (needsAddress) {
      return calculateStripeClickResolveDetails({
        needsAddress,
        phoneNumberRequired: this.hasPhoneNumber(),
        lineItems: this.summaryLineItems,
        // The total includes shipping and the discount while `line_items` has neither, so both are
        // named here: an unnamed rate shows as "Taxes", an unnamed discount drops the itemization.
        totalAmountCents: this.summaryTotalAmountCents,
        shippingOption: this.selectedShippingOption,
        discountAmountCents: this.summaryDiscountAmountCents,
      })
    }

    // Nothing an address could change, so the sheet opens on the final total.
    this.updateElementsAmount(totalAmountCents)
    return calculateStripeClickResolveDetails({
      needsAddress,
      phoneNumberRequired: this.hasPhoneNumber(),
      lineItems: this.summaryLineItems,
      totalAmountCents,
      discountAmountCents: summary.data?.discount?.amount_cents,
    })
  }

  // Settles the wallet's pending update exactly once, and never rejects: a handler that leaves
  // the sheet unsettled is not an error the wallet can show, it is a ~30s "Payment Not Completed"
  // timeout.
  async sendExpressCheckoutOrderPreview(
    resolve: (resolveDetails?: ChangeResolveDetails) => void,
    reject: () => void
  ): Promise<void> {
    let settled = false
    const settle = (settleWith: () => void): void => {
      if (settled) return
      settleWith()
      settled = true
    }

    try {
      const cart = this.store.checkoutCart.get()
      const dataToPreview: OrderPreviewPayload = {
        cart: cart,
        address: this.digitalPaymentData.address,
        integrations_stripe_installation_id: stripeInstallationId,
      }

      const couponState = this.store.coupons.state.get()
      let couponCode
      if (couponState == CouponStates.APPLYING) {
        couponCode = this.store.coupons.currentCode.get()
      } else {
        couponCode = this.store.coupons.appliedCode.get()
      }
      dataToPreview.coupon_code = couponCode
      dataToPreview.shippingOption = this.formatExpressShippingOption(this.digitalPaymentData.shippingRateId)

      const response = await fetchOrderPreview(dataToPreview)
      if (isResponseError(response)) {
        settle(reject)
      } else {
        settle(() => this.handleOrderPreviewForExpressCheckout(response, resolve))
      }
    } catch (error) {
      // `settled` is only set once the callback has returned, so a `resolve` Stripe refused
      // (it throws synchronously) still leaves the reject available here.
      settle(reject)
      // Constant message so Honeybadger groups every occurrence into one fault, as pushSetupError does.
      globalThis.Honeybadger?.notify('StripeCheckout express preview failed', {
        context: {
          error: (error as Error)?.message ?? String(error),
          errorName: (error as Error)?.name,
          checkoutState: this.getState(),
          summaryState: this.store.summary.get().state,
        },
      })
    }
  }

  mountExpress(): void {
    if (this.montedElements.expressCheckout.mounted) return

    this.mountElement('expressCheckout', {
      paymentMethods: { link: this.component.payWithLinkEnabled ? 'auto' : 'never' },
    })
    this.montedElements.expressCheckout.element?.on('confirm', (evt) => {
      const shippingOption = this.formatExpressShippingOption(evt.shippingRate?.id)
      // Without the address step there is no shipping address; the wallet's billing details carry
      // the same name and address, and they are what the next step's prefill needs.
      this.storeShippingDataFromExpress(evt.shippingAddress ?? evt.billingDetails)
      this.handleSubmit(shippingOption, true, undefined, evt.billingDetails?.name || evt.shippingAddress?.name)
    })
    this.montedElements.expressCheckout.element?.on('click', ({ resolve }) => {
      // This is the one wallet event with no `reject`, and building the details moves the
      // Elements amount — so a throw here would leave the click unsettled, which is the same
      // ~30s "Payment Not Completed" timeout the rest of this file exists to remove. Fall back
      // to the address round trip, which asks nothing of the summary.
      let resolveDetails: ClickResolveDetails
      try {
        resolveDetails = this.expressClickResolveDetails()
      } catch (error) {
        resolveDetails = calculateStripeClickResolveDetails({
          needsAddress: true,
          phoneNumberRequired: this.hasPhoneNumber(),
          lineItems: this.summaryLineItems,
          totalAmountCents: this.summaryTotalAmountCents,
          shippingOption: this.selectedShippingOption,
          discountAmountCents: this.summaryDiscountAmountCents,
        })
        // Constant message so Honeybadger groups every occurrence into one fault, as pushSetupError does.
        globalThis.Honeybadger?.notify('StripeCheckout express click failed', {
          context: {
            error: (error as Error)?.message ?? String(error),
            errorName: (error as Error)?.name,
            checkoutState: this.getState(),
            summaryState: this.store.summary.get().state,
          },
        })
      }
      resolve(resolveDetails)
    })

    this.montedElements.expressCheckout.element?.on('shippingaddresschange', async ({ address, resolve, reject }) => {
      this.digitalPaymentData.address = convertAddressFromStripe(address as StripeAddress)
      await this.sendExpressCheckoutOrderPreview(resolve, reject)
    })
    this.montedElements.expressCheckout.element?.on('cancel', () => {
      this.updateElementsAmount(this.summaryTotalAmountCents)
    })

    this.montedElements.expressCheckout.element?.on('shippingratechange', async ({ shippingRate, resolve, reject }) => {
      this.digitalPaymentData.shippingRateId = shippingRate.id
      await this.sendExpressCheckoutOrderPreview(resolve, reject)
    })

    this.montedElements.payment.element?.on('loaderror', (error) => this.handleStripeElementLoadingError(error))
  }

  formatExpressShippingOption(id: string): ShippingOption {
    if (id && id != STRIPE_SKIP_SHIPPING_ID) {
      return JSON.parse(id)
    }
  }

  elementReady(name: string) {
    const stateElement = this.getStateElement()
    const loaderElementName = ['shipping', 'billing'].includes(name) ? 'address' : name
    const loaderElement = stateElement.querySelector(`[data-stripe-loading-element="${loaderElementName}"]`)
    const stripeElement = stateElement.querySelector(`.stripe-${name}`) as HTMLElement
    // NOTE: The update here is to fix EDI-2593 and EDI-2609, the issue is on stripe's side
    // but we found this workaround.
    this.stripeElements.update({ appearance: this.parsedAppearence })
    let obeserverTimeout
    const resizeObserver = new ResizeObserver(() => {
      clearTimeout(obeserverTimeout)
      obeserverTimeout = setTimeout(() => {
        resizeObserver.disconnect()
        // NOTE: The update here is to fix EDI-2593 and EDI-2609, the issue is on stripe's side
        // but we found this workaround.
        this.stripeElements.update({ appearance: this.parsedAppearence })
        loaderElement.classList.add('forceHide')
        stripeElement.classList.remove('elAbsolute')
        stripeElement.style.opacity = '1'
      }, 500)
    })
    observeStripeElement(resizeObserver, stripeElement, loaderElement)
  }

  oneTimeListeners(): void {
    const signOutButtonthis = this.component.element.querySelector('.sign-out-button') as HTMLElement
    signOutButtonthis.addEventListener('click', () => {
      fetch('/contacts/sign_out', {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'same-origin',
      })
        .then(() => this.ensureStripeSession())
        .then((session) => {
          if (this.isLiveInstance()) {
            this.store.checkout.step.set(0)
            this.setState('guest')

            this.initializeElements(session, this.summaryTotalAmountCents)
            // NOTE: The timeout here is to fix EDI-2607, the issue is on stripe's side
            // but we found this workaround.
            setTimeout(() => {
              this.updateElementsDisplay()
              const cart = this.store.checkoutCart.get() as CheckoutCart
              CheckoutExistingOrders.clearUpdatableOrders(cart.items[0]?.product_id)
            }, 1000)
          }
        })
    })
    this.component.getComponents('Input/V1').forEach((inputComponent) => {
      const input = inputComponent.element.querySelector('input')
      const inputName = input.getAttribute('name')
      if (!['first_name', 'last_name', 'phone_number'].includes(inputName)) return
      if (inputName == 'phone_number') {
        IntlTel_initPhoneInput(input, { initialCountry: globalThis.cfVisitorData?.country ?? 'us' })
      }
      this.guestInputFields[inputName] = input
      ;['input', 'focus'].forEach((eventName) => {
        input.addEventListener(eventName, () => {
          clearInputError(this.errorInputForInput(input))
        })
      })

      input.addEventListener('blur', (event) => {
        const input = event.target as HTMLInputElement
        let value = input.value
        if (inputName == 'phone_number') {
          value = (input as any).iti.getNumber()
        }
        this.checkoutData.contact[inputName] = value
        this.updateInputState(inputName, value)
        this.updatePaymentElementDefaultValues()
      })
    })
    this.store.checkoutCart.subscribe(() => {
      this.updateElementsDisplay()
      this.sendOrderPreview()
      if (this.submitedOnce) {
        this.clearErrors('products')
        this.showErrors()
      }
    })
    this.listenAndSurfaceSummaryErrors()
  }

  // Mirrors Rebilly's `listenAndUpdateCheckoutErrors` (rebilly-checkout.ygg): when an
  // order-preview error lands on `summary.error.message`, surface it via the same
  // `.checkout-general-errors-wrapper` that submit-time errors use.
  listenAndSurfaceSummaryErrors(): void {
    this.store.summary.subscribe((summary: any) => {
      const message =
        summary?.state === SummaryStates.ERROR && summary?.error?.message
          ? summary.error.message
          : null

      if (this.summaryErrorEntry) {
        const idx = this.errors.globalErrors.indexOf(this.summaryErrorEntry)
        if (idx >= 0) this.errors.globalErrors.splice(idx, 1)
        this.summaryErrorEntry = null
      }

      if (message) {
        this.summaryErrorEntry = { message }
        this.errors.globalErrors.push(this.summaryErrorEntry)
      }

      this.showErrors()
    })
  }

  updateElementsDisplay(): void {
    this.cartAddressUpdates()
    this.updatePaymentElement()
    this.updateShippingOptionsDisplay()
    this.updateSubmitButton()
  }

  updatePaymentElement(): void {
    if (this.hasPaymentElement()) {
      this.mountPayment()
      this.mountExpress()
      // NOTE: due to a bug in stripe we need to mount Link as the last stripe element
      // while that is not fixed. Relevant story: https://linear.app/clickfunnels/issue/EDI-2619
      this.mountLink()
      this.getStateElement().querySelector('.stripe-payment-container')?.classList.remove('forceHide')
      this.getStateElement().querySelector('.stripe-expressCheckout-container')?.classList.remove('forceHide')
    } else {
      this.unmountElement('linkAuthentication')
      this.unmountElement('payment')
      this.unmountElement('expressCheckout')
      this.getStateElement().querySelector('.stripe-payment-container')?.classList.add('forceHide')
      this.getStateElement().querySelector('.stripe-expressCheckout-container')?.classList.add('forceHide')
    }
  }

  cartAddressUpdates(): void {
    const cart = this.store.checkoutCart.get()
    const hasPhysical = hasPhysicalProducts(cart)
    if (this.oldHasPhysical != hasPhysical) {
      this.updateWithShippingAddress(cart)
    }

    if (this.getState() == 'oto') {
      if (this.otoRequiresShippingForm() || this.paymentDeclined) {
        this.mountAddress()
        this.getStateElement().querySelector('.stripe-address')?.classList.remove('forceHide')
      } else {
        this.getStateElement().querySelector('.stripe-address')?.classList.add('forceHide')
      }

      if (hasUpgradeDowngradeItems(cart)) {
        this.getStateElement()
          .querySelector('[data-page-element="CheckoutProductSelectBump/V2"]')
          ?.classList.add('forceHide')
      } else {
        this.getStateElement()
          .querySelector('[data-page-element="CheckoutProductSelectBump/V2"]')
          ?.classList.remove('forceHide')
      }
    } else if (hasUpgradeDowngradeItems(cart)) {
      this.getStateElement().querySelector('.stripe-address')?.classList.add('forceHide')
    } else {
      this.mountAddress()
      this.getStateElement().querySelector('.stripe-address')?.classList.remove('forceHide')
    }
    this.oldHasPhysical = hasPhysical
  }

  updateSubmitButton(): void {
    // A cart change repaints the button, and the checkout stays interactive
    // behind the overlay when the merchant turned it off. Repainting mid-submit
    // would reset "Submitting..." back to the idle label and release the submit
    // lock while the payment is still running.
    if (this.submitInFlight) return

    const cart = this.store.checkoutCart.get()
    if (hasUpgradeDowngradeItems(cart)) {
      this.submitButtons.update(this.component.buttonUpgradeDowngrade)
    } else {
      this.submitButtons.setButtonInitialHTML()
    }
  }

  get addressOptions(): StripeAddressElementOptions {
    const hasPhysical = hasPhysicalProducts(this.store.checkoutCart.get())
    const options: StripeAddressElementOptions = {
      mode: hasPhysical ? 'shipping' : 'billing',
      fields: { phone: this.hasPhoneNumber() ? 'always' : 'never' },
      validation: this.hasPhoneNumber() ? { phone: { required: 'always' } } : {},
      display: {
        name: 'split',
      },
    }

    options.autocomplete = { mode: 'automatic' }

    options.defaultValues = {}
    if (Object.keys(this.checkoutData.address).length || this.getState() == 'oto') {
      options.defaultValues.address = convertToStripeAddress(this.checkoutData.address as CheckoutDataAddress)
    }

    const { first_name, last_name, phone_number } = this.checkoutData.contact ?? {}
    if (options.display.name == 'split') {
      if (first_name) options.defaultValues.firstName = first_name
      if (last_name) options.defaultValues.lastName = last_name
    } else {
      if (first_name) {
        options.defaultValues.name = first_name
        if (last_name) {
          options.defaultValues.name += ` ${last_name}`
        }
      }
    }

    if (this.checkoutData.contact.phone_number && this.hasPhoneNumber()) {
      options.defaultValues.phone = this.checkoutData.contact.phone_number
    }

    if (options.defaultValues.address && first_name && last_name) {
      const phoneNumberObj = this.hasPhoneNumber() ? { phone: phone_number } : {}
      options.contacts = [
        { name: `${first_name} ${last_name}`, address: options.defaultValues.address as any, ...phoneNumberObj },
      ]
    }

    return options
  }

  hasPhoneNumber(): boolean {
    const showPhoneNumber = this.component.contactFields.includes('phone_number')
    return showPhoneNumber
  }

  stripeElementHasError(type: keyof StripeMountedElementsData): boolean {
    return !!this.montedElements[type].lastChangeEvent && !this.montedElements[type].lastChangeEvent?.complete
  }

  shouldClearFormErrors(type: keyof StripeMountedElementsData): boolean {
    return !this.submitedOnce && this.stripeElementHasError(type)
  }

  clearStripeElement(type: keyof StripeMountedElementsData): void {
    console.log('[Stripe][clearStripeElement] =>', type)
    this.montedElements[type].element?.clear()
  }

  processCheckoutError(errorsByForm: Record<FormName, CheckoutFormErrors>): boolean {
    for (const [, errors] of Object.entries(errorsByForm)) {
      for (const [field, error] of Object.entries(errors?.fields ?? {})) {
        if (!error) continue
        this.pushFieldError(field, error.message)
      }

      if (errors?.globalErrors?.length) {
        this.pushGlobalError(errors.globalErrors[0].message)
      }
    }

    return !hasErrors(this.errors)
  }

  shippingOptionErrors(): CheckoutFormErrors {
    const state = this.component.getComponent(CheckoutShippingProfileSelectV1)
    const cart = this.store.checkoutCart.get()
    if (hasUpgradeDowngradeItems(cart)) {
      return null
    } else if (state) {
      const { shippingOptions, shippingOption, loadingShipping } = state
      return shippingOptionErrors(loadingShipping, cart, shippingOptions, shippingOption, true, null)
    }
  }

  tosErrors(): CheckoutFormErrors {
    const state = this.component.getComponent(CheckoutTosV1)
    if (state) {
      return tosErrors(state.accepted, true, true)
    }
  }

  contactErrors(): CheckoutFormErrors {
    return contactErrors(this.checkoutData.contact, Object.keys(this.guestInputFields))
  }

  stripeFormErrors(type: keyof StripeMountedElementsData): CheckoutFormErrors {
    if (this.shouldValidateStripeElements() && this.stripeElementHasError(type)) {
      return { fields: { [type]: { message: 'has error' } } }
    }
  }

  formErrorsByFormName(formName: FormName): CheckoutFormErrors {
    switch (formName) {
      case 'products':
        return productErrors(this.store.checkoutCart.get(), true)
      case 'shippingOption':
        return this.shippingOptionErrors()
      case 'tos':
        return this.tosErrors()
      case 'contact':
        return this.contactErrors()
    }
  }

  async validateStep(step: number): Promise<boolean> {
    this.clearAllErrors()

    const formErrors = {} as Record<FormName, CheckoutFormErrors>
    const curretStepForms = FORM_STRUCTURE_BY_TOTAL_STEPS[this.getTotalSteps()][step]
    let hasStripeElementInStep = false
    for (const field of curretStepForms) {
      if (stripeElementsForms.includes(field)) {
        hasStripeElementInStep = true
        formErrors[field] = this.stripeFormErrors(field)
      } else {
        formErrors[field] = this.formErrorsByFormName(field)
      }
    }

    const formValid = this.processCheckoutError(formErrors)
    if (!formValid && hasStripeElementInStep) {
      await this.stripeElements.submit()

      if (this.shouldClearFormErrors('billing')) this.clearStripeElement('billing')
      if (this.shouldClearFormErrors('shipping')) this.clearStripeElement('shipping')
      if (this.shouldClearFormErrors('payment')) this.clearStripeElement('payment')
    }

    const customFieldsValid = checkValidCustomFields(this.component.element)
    const valid = formValid && customFieldsValid

    if (!valid) {
      this.showErrors({ shouldScrollToError: true })
    }

    return valid
  }

  updateShippingOptionsDisplay(): void {
    const stateElement = this.getStateElement()
    const cart = this.store.checkoutCart.get()
    if (hasPhysicalProducts(cart) && !hasUpgradeDowngradeItems(cart)) {
      stateElement.querySelector('.shipping-options-container')?.classList.remove('forceHide')
      this.shippingComponents.show()
    } else {
      this.shippingComponents.hide()
      stateElement.querySelector('.shipping-options-container')?.classList.add('forceHide')
    }
  }

  selectShippingOption(option: ShippingOption): void {
    this.selectedShippingOption = option
    this.shippingComponents.setOption(option)
    this.sendOrderPreview()
    if (this.submitedOnce) {
      this.clearErrors('shippingOption')
      this.showErrors()
    }
  }

  updateShippingOptions(update: CheckoutShippingProfileSelectUpdate): void {
    this.shippingComponents.update(update)
  }

  async sendOrderPreview(options?: {
    skipUpdatingStripe?: boolean
    throttle: boolean
    isCouponValidation?: boolean
    skipThrottle?: boolean
    forceOrderPreview?: boolean
  }): Promise<void> {
    const { skipUpdatingStripe, throttle, isCouponValidation, skipThrottle, forceOrderPreview } = options ?? {}
    if (this.preventOrderPreview && !forceOrderPreview) return

    const { summary } = this.store

    const cart = this.store.checkoutCart.get()

    if (!cart.items?.length) {
      if (this.store.coupons.appliedCode.get() || this.store.coupons.currentCode.get()) {
        this.store.coupons.appliedCode.set('')
        this.store.coupons.currentCode.set('')
        this.store.coupons.state.set(CouponStates.READY)
      }
      summary.set({ state: SummaryStates.WAITING })
      return
    }

    const couponStateBeforeApplying = this.store.coupons.state.get()
    if (isCouponValidation && couponStateBeforeApplying != CouponStates.APPLYING) {
      this.couponStateBeforeApplying = couponStateBeforeApplying
      this.store.coupons.state.set(CouponStates.APPLYING)
    }

    const payload: OrderPreviewPayload = this.hasValidAddress(this.checkoutData.address)
      ? {
          address: this.checkoutData.address,
          shippingOption: this.selectedShippingOption,
          cart,
        }
      : { cart }
    payload.integrations_stripe_installation_id = stripeInstallationId

    // TODO: Maybe we can just check `isCouponValidation`?
    const couponState = this.store.coupons.state.get()
    let couponCode
    if (couponState == CouponStates.APPLYING) {
      couponCode = this.store.coupons.currentCode.get()
    } else {
      couponCode = this.store.coupons.appliedCode.get()
    }
    payload.coupon_code = couponCode

    const canUpdateSummary = this.hasValidDataForSummaryUpdate(payload)
    const canFetchLineItemSubtotal = hasValidDataForLineItemSubtotal(payload)

    if (!canUpdateSummary) {
      summary.set({
        state: SummaryStates.WAITING,
      })
    }

    if (canUpdateSummary && !canFetchLineItemSubtotal) {
      return
    }

    this.updatingExpressStarted()
    // TODO: Move this debounce logic to projects/user_pages/app/javascript/lander/utils/fetcher.ts as both
    // the rebilly and stripe checkout are using a similar logic
    clearTimeout(this.sendOrderPreviewTimer)
    const sendOrderPreviewFn = async () => {
      const response = await fetchOrderPreview(payload, () => {
        if (canUpdateSummary) {
          if (hasPhysicalProducts(cart)) {
            this.shippingComponents.setLoading()
          }
          summary.set({
            state: SummaryStates.CALCULATING,
          })
        }
      })

      if (canFetchLineItemSubtotal && !isResponseError(response)) {
        this.summaryLineItems = response.line_items
        this.summaryTotalAmountCents = response.total_amount_cents
        this.summaryDiscountAmountCents = response.discount_amount_cents

        // Discounts can be applied for both express and normal payment methods
        const currentCode = this.store.coupons.currentCode.get()
        const couponCodeApplied = response.discounts?.find(
          (discount) => discount.code?.toUpperCase() == currentCode?.toUpperCase()
        )
        if (couponCodeApplied) {
          this.store.coupons.appliedCode.set(currentCode)
          this.store.coupons.state.set(CouponStates.APPLIED)
        } else {
          this.store.coupons.state.set(CouponStates.READY)
        }

        if (!skipUpdatingStripe) {
          this.updateElementsAmount(response.total_amount_cents)
        }
      } else if (isBackendError(response)) {
        this.updatingExpressEnded()
        if (isResponseError(response)) {
          this.handleOrderPreviewErrorForExpress(response.error, skipUpdatingStripe)
        }
      }

      // TODO: Currently, if there is a coupon code we render all summary details
      // but this won't include shipping / taxes if they are not there.
      // We can inform contacts they need to fill the form to be able to calculate them.
      // (I also think we should always present summary with totals leaving shipping and taxes
      // in a "to be calculated" state instead of current implementation that we don't show them)
      if (canUpdateSummary || couponCode) {
        if (isResponseError(response)) {
          if (isBackendError(response)) {
            this.handleOrderPreviewErrorForSummary(response.error)
          }
        } else {
          const summaryResponse = processOrderPreviewResponseForSummary(response)
          const { shipping_quotes_response, selected_shipping_option } = response
          this.selectedShippingOption = selected_shipping_option
          summary.set({
            state: SummaryStates.OK,
            data: summaryResponse,
          })
          if (hasPhysicalProducts(cart)) {
            this.shippingComponents.setShippingOptions(
              shipping_quotes_response?.options ?? [],
              selected_shipping_option
            )
          }
        }
      }
    }

    if (skipThrottle) {
      return await sendOrderPreviewFn()
    } else {
      this.sendOrderPreviewTimer = setTimeout(
        async () => {
          await sendOrderPreviewFn()
        },
        throttle ? 500 : 0
      )
    }
  }

  startLoadingState(): void {
    this.submitInFlight = true
    this.loadingComponents.show()
    this.submitButtons.setSubmittingText()
  }

  removeLoadingState(): void {
    this.submitInFlight = false
    this.loadingComponents.hide()
    this.submitButtons.setButtonInitialHTML()
  }

  handleStripeError(error: StripeError): string {
    // A checkout with no payment element only has the stored instrument. If Stripe refused that
    // instrument, the recovery is a card field; any other Stripe error leaves the instrument in
    // play and the buyer retries with it.
    if (!this.hasPaymentElement() && STRIPE_INSTRUMENT_REJECTION_CODES.has(error.code)) {
      this.paymentDeclined = true
      this.updateElementsDisplay()
    }
    return error.message
  }

  handleOrderPreviewErrorForExpress(response: any, skipUpdatingStripe?: boolean): void {
    const errorDetail = response.details?.[0]
    if (errorDetail?.type == 'InvalidCouponError') {
      this.store.coupons.appliedCode.set('')
      this.store.coupons.currentCode.set('')
      this.store.coupons.errorMessage.set(errorDetail.message)
      this.store.coupons.state.set(CouponStates.ERROR)
      // Both codes are cleared, so the error's undiscounted preview, which the summary shows, is what
      // will be charged.
      const preview: ServerRespSummaryData = errorDetail.preview
      this.summaryLineItems = preview.line_items
      this.summaryTotalAmountCents = preview.total_amount_cents
      this.summaryDiscountAmountCents = preview.discount_amount_cents
      if (!skipUpdatingStripe) {
        this.updateElementsAmount(preview.total_amount_cents)
      }
    } else {
      globalThis.Checkout.store.coupons.state.set(this.couponStateBeforeApplying)
    }
  }

  handleOrderPreviewErrorForSummary(response: any): void {
    const errorDetail = response.details?.[0]
    if (errorDetail?.type == 'InvalidCouponError') {
      const summaryResponse = processOrderPreviewResponseForSummary(errorDetail.preview)
      this.store.summary.set({
        state: SummaryStates.OK,
        data: summaryResponse,
      })
    } else if (errorDetail?.message) {
      this.store.summary.set({
        state: SummaryStates.ERROR,
        error: { message: errorDetail.message },
      })
    } else {
      // Same fallback Rebilly's summaryErrors uses. Without it the summary listener has nothing to
      // surface, and an ERROR state opens a checkout with no explanation in it.
      this.store.summary.set({
        state: SummaryStates.ERROR,
        error: { message: 'There was an error calculating your order total' },
      })
    }
  }

  handleError(error: any, skipRemoveLoadingState = false): void {
    const GENERIC_PAYMENT_ERROR =
      'There was a problem processing your payment. Please try again or use a different payment method.'

    console.error('Error Submiting Payment:', error)
    if (!skipRemoveLoadingState) {
      this.removeLoadingState()
    }
    let displayMessage = ''
    let actionable = true
    if (isStripeError(error)) {
      displayMessage = this.handleStripeError(error)
    } else {
      displayMessage = serverCheckoutErrorMessage(error) ?? GENERIC_PAYMENT_ERROR
      actionable = isActionableCheckoutError(error)
    }
    this.lastSubmitFailure = { message: displayMessage, actionable }
    this.setSubmitNotification('Failed Submit', displayMessage)
  }

  async processStripeResponse(next_url: string, { error, paymentIntent }: PaymentIntentResult) {
    if (error) {
      this.handleError(error)
    } else if (paymentIntent.status === 'succeeded') {
      window.location.href = next_url
    } else if (paymentIntent.status === 'processing') {
      // NOTE: According to the documentation the paymentIntent can return processing
      // so we should handle it trying to query the payment intent each 1 second
      // Should we have a max amount of time if stripe actually says it is processing?
      // I do not think so. The best course of action if Stripe is not broken is to keep pooling
      // Or create a websocket connection with our server and wait for a webhook.
      setTimeout(async () => {
        const result = await this.stripe.retrievePaymentIntent(paymentIntent.client_secret)
        this.processStripeResponse(next_url, result)
      }, 1000)
    } else {
      // NOTE: This was added to deal with a problem in the Stripe when dealing with CashApp
      // and the user closes the modal. In that case they just return the payment intent
      // with status `requires_action`. Eventhough the API docs says it will always either return
      // ans error or a payment intent with status 'succeeded'/'processing'
      // issue: https://linear.app/clickfunnels/issue/ENG-4288/closing-cash-app-modal-redirects-to-next-funnel-page
      //
      // However, voucher-based methods (konbini, boleto, oxxo, multibanco) also remain in
      // requires_action after displaying payment instructions. Unlike CashApp, these represent
      // pending payments — the customer has seen the instructions and should advance to the
      // next page. We distinguish them by checking next_action.type on the paymentIntent.
      const voucherNextActions = ['konbini_display_details', 'boleto_display_details', 'oxxo_display_details', 'multibanco_display_details']
      const nextActionType = paymentIntent.next_action?.type
      if (paymentIntent.status === 'requires_action' && nextActionType && voucherNextActions.includes(nextActionType)) {
        window.location.href = next_url
      } else {
        this.removeLoadingState()
      }
    }
  }

  async handleServerResponse(response: FetcherResponse<SubmitResponse>) {
    if (isResponseError(response)) {
      this.handleError(response.error)
      if (isRefusedCouponError(response.error)) await this.revalidateAppliedCoupon()
    } else {
      if (response.payment_intent?.status === 'requires_action') {
        // Redirect to 3D Secure page /  Show 3DS secure 2 modal.
        const result: PaymentIntentOrSetupIntentResult = await this.stripe.handleNextAction({
          clientSecret: response.payment_intent.client_secret,
        })

        if (result.error) {
          this.handleError(result.error)
        } else if ('setupIntent' in result && result.setupIntent) {
          // Free products use a SetupIntent for 3DS verification.
          // handleNextAction returns { setupIntent } instead of { paymentIntent }.
          if (result.setupIntent.status === 'succeeded') {
            window.location.href = response.next_url
          } else {
            this.removeLoadingState()
          }
        } else {
          await this.processStripeResponse(response.next_url, result as PaymentIntentResult)
        }
      } else if (response.next_url) {
        window.location.href = response.next_url
      } else {
        this.handleError(response)
      }
    }
  }

  // The purchase refused the applied code, which stopped applying after the buyer applied it. Price
  // the cart with it again: the preview refuses it too, which drops the code and shows the total the
  // next attempt will charge. The cached preview would answer the unchanged payload with the old total.
  async revalidateAppliedCoupon(): Promise<void> {
    this.store.coupons.currentCode.set(this.store.coupons.appliedCode.get())
    forgetLastOrderPreview()
    await this.sendOrderPreview({
      throttle: false,
      isCouponValidation: true,
      skipThrottle: true,
      forceOrderPreview: true,
    })
  }

  shouldIncludeRegistration(): boolean {
    return document.getElementsByName('registration[calendar_event_id]')?.length > 0
  }

  buildRegistrationDetails(): { calendar_event_id: string; occurs_at: string } {
    return {
      calendar_event_id: (document.getElementsByName('registration[calendar_event_id]')[0] as HTMLInputElement).value,
      occurs_at: (document.getElementsByName('registration[occurs_at]')[0] as HTMLInputElement).value,
    }
  }

  buildSubmitPayload(
    shippingOption: ShippingOption,
    idempotencyKey: string,
    confirmationToken?: ConfirmationToken,
    redirectOverride?: string
  ): SubmitPayload {
    const customFields = buildCustomFields()
    const cart = this.store.checkoutCart.get()
    const coupon = this.store.coupons.appliedCode.get()
    const defaultRedirect = this.element
      .querySelector('[href="#submit-checkout-form"]')
      ?.getAttribute('data-on-submit-go-to')
    const redirect_to = redirectOverride || defaultRedirect
    const payload: SubmitPayload = {
      ...(customFields ? customFields : {}),
      payment_processor: 'stripe',
      integrations_stripe_installation_id: stripeInstallationId,
      request_idempotency: idempotencyKey,
      skip_optin_track: document.getElementById('cfAR')?.getAttribute('data-leads-collected') === 'true',
      purchase: {
        coupon_codes: [coupon].filter(Boolean),
        process_new_order: true,
        selected_shipping_option: shippingOption,
        order_id: cart.order_id,
        product_variants: cart.items.map(({ variant_id, price_id, line_item_id, quantity }) => {
          return {
            id: variant_id,
            quantity,
            price_id,
            ...(line_item_id ? { line_item_id } : {}),
          }
        }),
      },
    }

    if (redirect_to) {
      payload.redirect_to = redirect_to
    }

    if (confirmationToken) {
      payload.token = confirmationToken.id
    } else if (hasUpgradeDowngradeItems(cart)) {
      payload.contact = {
        email: globalThis.sessionData.contact.email,
        first_name: globalThis.sessionData.contact.first_name,
        last_name: globalThis.sessionData.contact.last_name,
        phone_number: globalThis.sessionData.contact.phone_number,
      }
    } else {
      payload.contact = {
        email: globalThis.sessionData.contact.email,
        first_name: this.checkoutData.contact.first_name,
        last_name: this.checkoutData.contact.last_name,
        phone_number: this.checkoutData.contact.phone_number,
      }
      if (Object.keys(this.checkoutData.address).length) {
        payload.contact.shipping_addresses_attributes = [
          {
            physical_address_attributes: {
              address_one: this.checkoutData.address.address_one,
              address_two: this.checkoutData.address.address_two,
              city: this.checkoutData.address.city,
              region_name: this.checkoutData.address.state,
              country_id: this.checkoutData.address.country,
              postal_code: this.checkoutData.address.postal_code,
            },
          },
        ]
      }
    }

    if (this.shouldIncludeRegistration()) payload['registration'] = this.buildRegistrationDetails()

    return payload
  }

  otoRequiresShippingForm(): boolean {
    const hasPhysical = hasPhysicalProducts(this.store.checkoutCart.get())
    return hasPhysical && !globalThis.sessionData.contact.shipping_addresses?.[0]
  }

  hasPaymentElement(): boolean {
    return (
      (!hasUpgradeDowngradeItems(this.store.checkoutCart.get()) && ['guest', 'saved'].includes(this.getState())) ||
      this.paymentDeclined
    )
  }

  shouldValidateStripeElements(): boolean {
    return this.hasPaymentElement() || this.otoRequiresShippingForm()
  }

  hasStripeToken(): boolean {
    return this.hasPaymentElement()
  }

  async handleSubmit(
    shippingOption: ShippingOption,
    isExpress?: boolean | string,
    redirectOverride?: string,
    walletName?: string
  ): Promise<boolean> {
    this.removeSubmitNotification()
    this.lastSubmitFailure = null
    this.submitedOnce = true
    if (!isExpress && !(await this.validateStep(this.store.checkout.step.get()))) {
      return false
    }
    if (!isExpress && !checkValidCustomFields(this.component.element)) {
      return false
    }

    if (!isExpress) {
      if (this.shouldValidateStripeElements()) {
        const submitError = await this.stripeElements.submit()
        if (submitError.error) {
          console.log('[Stripe] submitError =>', submitError)
          return false
        }
      }
      if (this.store.summary.get().state != SummaryStates.OK) return false
    }

    this.startLoadingState()

    let confirmationToken
    if (isExpress || this.hasStripeToken()) {
      const confirmationTokenResult = await this.stripe.createConfirmationToken({
        elements: this.stripeElements,
        params: {
          payment_method_data: {
            billing_details: {
              name: confirmationTokenBillingName(this.checkoutData.contact, walletName),
              phone: this.checkoutData.contact.phone_number,
              email: this.checkoutData.contact.email,
            },
          },
        },
      })
      if (confirmationTokenResult.error) {
        this.handleError(confirmationTokenResult.error)
        return false
      }
      confirmationToken = confirmationTokenResult.confirmationToken
    }

    const payload = this.buildSubmitPayload(shippingOption, uuidv4(), confirmationToken, redirectOverride)
    const response = await postSubmit(payload)
    await this.handleServerResponse(response)
    return !this.lastSubmitFailure
  }

  hasValidAddress(address: Address): boolean {
    let addressErrorsVal
    const cart = this.store.checkoutCart.get()
    if (
      (!hasPhysicalProducts(cart) && !this.component.billingEnabled) ||
      (hasUpgradeDowngradeItems(cart) && !this.hasPaymentElement())
    ) {
      addressErrorsVal = null
    } else if (this.getState() == 'oto' && !hasPhysicalProducts(cart) && !this.hasPaymentElement()) {
      addressErrorsVal = addressErrors(address ?? {}, true, [])
    } else {
      let requiredFields = ['address_one', 'city', 'state', 'country', 'postal_code']
      const stripeAddressFieldExceptions = globalThis.globalResourceData.stripeAddressFieldExceptions
      if (address?.country && stripeAddressFieldExceptions.postalCodeless.includes(address?.country)) {
        requiredFields = requiredFields.filter((field) => field !== 'postal_code')
      }
      if (address?.country && stripeAddressFieldExceptions.cityless.includes(address?.country)) {
        requiredFields = requiredFields.filter((field) => field !== 'city')
      }
      if (address?.country && stripeAddressFieldExceptions.regionless.includes(address?.country)) {
        requiredFields = requiredFields.filter((field) => field !== 'state')
      }
      addressErrorsVal = addressErrors(address ?? {}, true, requiredFields)
    }

    return !hasErrors(addressErrorsVal)
  }

  hasValidDataForSummaryUpdate(payload: OrderPreviewPayload): boolean {
    const { cart, address } = payload

    const productSelectErrors = productErrors(cart, true)
    const hasValidAddress = this.hasValidAddress(address)

    return !hasErrors(productSelectErrors) && hasValidAddress
  }

  // NOTE: for OTO this gets triggered after mounted.
  // We can enhance it later to only skip after this mounting change event.
  // Firstly we need to understand how to verify that
  removeSubmitNotificationStripeElementChange(): void {
    if (this.getState() != CheckoutStates.OTO) this.removeSubmitNotification()
  }
}
